import { Dropdown, MenuProps, Tag, Tooltip } from 'antd';
import React from 'react';
import { isEquivalentAttribute, parseAttributeToDisplayText, parseDisplayValueToAttribute } from 'pages/UserPage/components/MiddlePane/utils/displayAttributes';
import { updateAttributeSecondary } from './attributeTagUtils';
import { AddedAttribute, AddedAttributeType } from '../../../../../types';

export interface Props {
  value: string;
  label: React.ReactNode;
  isUsingDisplayValues: boolean;
  onClose: () => void;
  attributesForEdit: AddedAttribute[];
  attributeTypeName: string;
  userAttributeTypes: AddedAttributeType[];
  setUserAttributeTypes: (newUserAttrTypes: AddedAttributeType[]) => void;
}

/**
 * This component is to display an attribute as a tag inside a Select component.
 *
 * The `value` prop may be a "display text" or a display value" (which is a stringified version of the
 * attribute). The `isUsingDisplayValues` prop is used to determine whether the `value` prop is a display
 * text or a display value.
 *
 * Display values are needed when a tag's rendering depends on whether it's a primary or secondary attribute.
 */
const AttributeTag = ({
  value,
  label,
  isUsingDisplayValues,
  onClose,
  attributesForEdit,
  attributeTypeName,
  userAttributeTypes,
  setUserAttributeTypes,
}: Props) => {
  // find the attribute object that corresponds to the value, depending on whether't a
  // display text or a display value
  const attr = attributesForEdit.find(att => (isUsingDisplayValues ?
    isEquivalentAttribute(att, parseDisplayValueToAttribute(value))
    : parseAttributeToDisplayText(att) === value));

  // TODO what happens if attr cannot be found?
  if (!attr) {
    console.error(`AttributeTag: could not find attribute for value: ${value}`);
    return null;
  }

  const updateAttrSecondary = (is_secondary: boolean) => {
    updateAttributeSecondary(
      attributeTypeName,
      { is_secondary },
      attr,
      userAttributeTypes,
      setUserAttributeTypes
    );
  };

  const items: MenuProps['items'] = !attr?.id ? [
    { label: 'primary', key: 'primary', onClick: () => updateAttrSecondary(false) },
    { label: 'secondary', key: 'secondary', onClick: () => updateAttrSecondary(true) },
  ] : [
    {
      label: 'Existing attributes are uneditable. You must delete, save and add a new one.',
      key: 'uneditable',
      disabled: true,
    },
  ];

  // get the label to display in the tag
  const labelText = isUsingDisplayValues ? parseAttributeToDisplayText(attr) : label;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      key={value}
      // prevent the select dropdown from opening when clicking on the tag or tag dropdown
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Tooltip title={attr?.is_derived ? 'Derived attributes cannot be edited.' : ''}>
        <Dropdown menu={{ items }} placement="topLeft" trigger={['contextMenu']}>
          <Tag closable={!attr?.is_derived} color={attr?.is_secondary ? 'blue' : 'default'} onClose={onClose}>
            {labelText}
          </Tag>
        </Dropdown>
      </Tooltip>
    </div>
  );
};

export default AttributeTag;
